import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';

import { REQUEST_CANCEL_REASONS } from '../../../constants/general.constants';
import { errorHandler } from '../../services/authService';
import { required } from '../../utils/form-default-errors';
import { Button, IconButton, Select, TextArea, Typography } from '..';
import * as S from './DeclineResponsesModal.styles';
import { declineRequestResponse } from '../../services/requests';

export const DeclineResponsesModal = forwardRef(({ dispatch }, ref) => {
	const [isOpen, setIsOpen] = useState(false);
	const [shouldDeclineAll, setShouldDeclineAll] = useState(false);

	const {
		handleSubmit,
		control,
		register,
		setValue,
		reset,
		formState: { errors, isSubmitting },
	} = useForm();

	const handleOpen = ({ requestId, responseId, declineAll }) => {
		if (requestId) setValue('requestId', requestId);
		if (responseId) setValue('responseId', responseId);
		setValue('declineAll', declineAll);
		setShouldDeclineAll(declineAll);
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			reset();
		}, 200);
	};

	async function asyncCaller(data) {
		await declineRequestResponse(data);
		if (declineAll !== true) {
			dispatch({
				type: 'CLEAR_SELECTED_CONVERSATION',
			});
		}
	}

	const onSubmit = (e) => {
		handleSubmit(asyncCaller)(e).catch(errorHandler);
	};

	useImperativeHandle(
		ref,
		() => ({
			open: handleOpen,
			close: handleClose,
		}),
		[handleOpen, handleClose]
	);

	return (
		<ReactModal className="smaller-modal" ref={ref} closeTimeoutMS={200} isOpen={isOpen} onRequestClose={handleClose}>
			<S.Form onSubmit={onSubmit}>
				<IconButton className="close-button" type="button" icon={['fal', 'times']} onClick={handleClose} />
				<S.InfoWrapper>
					<Typography tag="h2" weight="extrablack" center>
						{shouldDeclineAll === true ? 'Decline All Other Responses' : 'Decline Response'}
					</Typography>
				</S.InfoWrapper>
				<S.FieldsWrapper>
					<Controller
						name="declinedReason"
						control={control}
						rules={{ required: required('Reason') }}
						render={({ field: { onChange, value } }) => (
							<Select label="Reason" placeholder="Type to search" id="cancel-reason" error={errors?.declinedReason} options={REQUEST_CANCEL_REASONS} value={REQUEST_CANCEL_REASONS.find((c) => c.value === value) || ''} onChange={(val) => onChange(val.value)} />
						)}
					/>
					<TextArea label="Notes" placeholder="Add notes here" id="cancel-notes" error={errors?.declinedNotes} {...register('declinedNotes')} />
				</S.FieldsWrapper>
				<S.ButtonsWrapper>
					<Button type="button" variant="outline" onClick={handleClose}>
						<Typography weight="bold" variation="button-medium">
							Back
						</Typography>
					</Button>
					<Button type="submit" variation="warning">
						<Typography weight="bold" variation="button-medium">
							{isSubmitting ? 'Loading' : shouldDeclineAll === true ? 'Decline All' : 'Decline'}
						</Typography>
					</Button>
				</S.ButtonsWrapper>
			</S.Form>
		</ReactModal>
	);
});

DeclineResponsesModal.propTypes = {
	dispatch: PropTypes.func,
};

DeclineResponsesModal.displayName = 'DeclineResponsesModal';
